'use client';

import { tv } from 'tailwind-variants';
import { faCalendarDays, faUserDoctor } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import {
  Professional,
  ProfessionalScheduleProcedures,
} from '@medprev-web-app/api-graphql-client';

import { Card } from '@/components/ui/card.component';
import { Icon } from '@/components/ui/icon.component';
import { Button } from '@/components/ui/button/button.component';
import { Link } from '@/components/ui/link/link.component';
import { formatToTitleCase } from '@/utils/formatters/format-to-title-case';
import { useAgendamentoNavigation } from '@/hooks/navigation/use-agendamento-navigation.hook';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

interface ProfessionalAsideCardProps extends React.HTMLAttributes<HTMLDivElement> {
  professional: Professional;
  procedure: ProfessionalScheduleProcedures[];
}

function resolveProfessionalName(professional: Professional): string {
  const { name, honorific } = professional;

  if (!honorific) {
    return `${formatToTitleCase(name!.split(' ')[0])}`;
  }

  return `${honorific} ${formatToTitleCase(name!.split(' ')[0])}`;
}

const professionalAsideCardStyle = tv({
  slots: {
    card: 'w-full xl:max-w-[32rem] 2xl:max-w-[32rem] flex-1 mt-4 xl:mt-0 rounded-[12px] px-10 py-8 sticky top-6',
  },
});

export function ProfessionalAsideCard({
  professional,
  procedure,
  className,
  ...rest
}: ProfessionalAsideCardProps) {
  const { card } = professionalAsideCardStyle();
  const { buildLink } = useAgendamentoNavigation();

  return (
    <Card
      className={card({ className })}
      {...rest}
      data-testid={DataTestIdEnum.PROFESSIONAL_PROFILE_ASIDE_CARD}
    >
      <div className="space-y-6">
        <div className="flex items-start gap-2">
          <Icon
            icon={faUserDoctor}
            className="text-2xl mt-1 md:mt-0.5 text-blue-600 w-6 h-6"
          />

          <div className="max-w-lg">
            <h3 className="text-xl font-semibold text-gray-800 break-words text-wrap hyphens-auto overflow-hidden">
              Agende uma consulta com
              {' '}
              {resolveProfessionalName(professional)}
            </h3>
          </div>
        </div>

        <Button
          data-testid={DataTestIdEnum.PROFESSIONAL_PROFILE_SCHEDULE_BUTTON}
          startIcon={faCalendarDays}
          data-analytics-event={AnalyticsEnum.PROFESSIONAL_PROFILE_SELECT_REDIRECT}
          asChild
        >
          <Link
            prefetch={false}
            href={buildLink({ path: `/professionals/${professional.id}/` })}
          >
            Consultas e horários
          </Link>
        </Button>

        {procedure.length > 0 && (
          <div>
            <span className="text-gray-700">
              Veja alguns atendimentos oferecidos:
            </span>

            <ul className="mt-2 list-disc space-y-2">
              {procedure.map((procedure, index) => (
                <li
                  key={index}
                  className="text-gray-700 ml-6"
                  data-testid={`${DataTestIdEnum.PROFESSIONAL_PROFILE_PROCEDURE_ITEM}-${index}`}
                >
                  {procedure.name}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Card>
  );
}
